import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, RefreshCw, ShieldCheck, Lock, TrendingUp, TrendingDown, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useGoldRate } from '../context/GoldRateContext';
import { useAuth } from '../context/AuthContext';
import { PanThresholdBanner } from '../components/kyc/PanThresholdBanner';
import api from '../services/api';
import UPIPaymentModal from '../components/payments/UPIPaymentModal';

// Rotating tips for Book Gold page
const bookGoldTips = [
  "Tip: Book small amounts daily to average out gold price fluctuations",
  "Tip: Gold has historically been a hedge against inflation",
  "Tip: Start with as little as ₹100 — every Milli counts!",
  "Tip: Your gold is stored securely with 22K purity guarantee",
  "Tip: No making charges when you book — pay only for pure gold",
  "Tip: Track your gold value anytime from your dashboard",
  "Tip: Gold rates update every few minutes — book when you see a good rate",
  "Tip: Consistent small bookings often beat timing the market",
  "Tip: Your Milli's never expire — redeem whenever you're ready",
];

const BookGoldPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated, loading: authLoading, refreshUser, user } = useAuth();
  const { buyRate, rate22kt, loading: rateLoading, refetch: refetchRate } = useGoldRate();
  
  const [amount, setAmount] = useState(1000);
  const [customAmount, setCustomAmount] = useState('1000');
  const [paymentMethod, setPaymentMethod] = useState('upi');
  const [isLoading, setIsLoading] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentData, setPaymentData] = useState(null);
  
  // Random tip on page visit
  const [currentTip] = useState(() => bookGoldTips[Math.floor(Math.random() * bookGoldTips.length)]);
  
  // Real data states
  const [priceHistory, setPriceHistory] = useState([]);
  const [priceHistoryLoading, setPriceHistoryLoading] = useState(true);
  const [platformStats, setPlatformStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);

  // Fetch 7-day gold rate history
  useEffect(() => {
    const fetchPriceHistory = async () => {
      try {
        const response = await api.get('/gold-rate/history?days=7&interval=daily');
        const data = response.data;
        
        if (data.history && data.history.length > 0) {
          // Transform API data to chart format
          const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
          const chartData = data.history.map((item, index) => {
            const date = new Date(item.timestamp);
            const isToday = index === data.history.length - 1;
            return {
              day: isToday ? 'Today' : dayNames[date.getDay()],
              price: item.buy_rate || item.rate_22k / 1000, // Use buy_rate (per milli)
              date: date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })
            };
          });
          setPriceHistory(chartData);
        }
      } catch (error) {
        console.error('Failed to fetch price history:', error);
        // Fallback to current rate only
        if (buyRate) {
          setPriceHistory([{ day: 'Today', price: buyRate }]);
        }
      } finally {
        setPriceHistoryLoading(false);
      }
    };

    fetchPriceHistory();
  }, [buyRate]);

  // Fetch platform stats
  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await api.get('/stats/platform');
        setPlatformStats(response.data);
      } catch (error) {
        console.error('Failed to fetch platform stats:', error);
      } finally {
        setStatsLoading(false);
      }
    };

    fetchStats();
  }, []);

  // Redirect unauthenticated users to home-new silently
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/home-new', { replace: true });
    }
  }, [isAuthenticated, authLoading, navigate]);

  // Show nothing while checking auth (prevents flash of content)
  if (authLoading || !isAuthenticated) {
    return null;
  }

  const presetAmounts = [100, 500, 1000, 5000, 10000, 50000, 100000, 500000];
  
  const millisYouGet = buyRate > 0 ? amount / buyRate : 0;
  const gramsYouGet = millisYouGet / 1000;

  // Calculate chart stats from real data
  const minPrice = priceHistory.length > 0 ? Math.min(...priceHistory.map(p => p.price)) : 0;
  const maxPrice = priceHistory.length > 0 ? Math.max(...priceHistory.map(p => p.price)) : 0;
  const priceRange = maxPrice - minPrice || 1;
  const weekChange = priceHistory.length >= 2 
    ? ((priceHistory[priceHistory.length - 1].price - priceHistory[0].price) / priceHistory[0].price * 100).toFixed(2)
    : '0.00';
  const isPositive = parseFloat(weekChange) >= 0;

  const handleAmountChange = (value) => {
    const numValue = parseFloat(value) || 0;
    setAmount(numValue);
    setCustomAmount(value);
  };

  const handlePresetClick = (preset) => {
    setAmount(preset);
    setCustomAmount(preset.toString());
  };

  const handleProceedToPay = async () => {
    if (!user?.kyc_verified) {
      toast.error('Please complete Aadhaar KYC verification before booking gold');
      navigate('/kyc-new');
      return;
    }
    // Validate amount
    if (amount < 1) {
      toast.error('Minimum amount is ₹1');
      return;
    }
    if (amount > 100000) {
      toast.error('Maximum amount is ₹1,00,000');
      return;
    }
    if (!buyRate || buyRate <= 0) {
      toast.error('Unable to fetch gold rate. Please refresh and try again.');
      return;
    }

    setIsLoading(true);

    try {
      // Refresh rate before payment to ensure latest rate
      await refetchRate();

      const response = await api.post('/upi/initiate', {
        amount_inr: amount,
        milli_amount: millisYouGet,
        rate_at_time: buyRate
      });

      if (response.data.success) {
        setPaymentData(response.data);
        setShowPaymentModal(true);
        toast.success('Payment initiated! Complete in your UPI app.');
      } else {
        toast.error(response.data.message || 'Failed to initiate payment');
      }
    } catch (error) {
      console.error('Payment initiation error:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to initiate payment';
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePaymentSuccess = async (data) => {
    // Refresh user data to update balance
    if (refreshUser) {
      await refreshUser();
    }
    // Close modal after a short delay
    setTimeout(() => {
      setShowPaymentModal(false);
      setPaymentData(null);
      // Reset form
      setAmount(1000);
      setCustomAmount('1000');
    }, 2000);
  };

  const handlePaymentModalClose = () => {
    setShowPaymentModal(false);
    // Don't clear paymentData immediately in case user wants to reopen
  };

  return (
    <div className="px-3 sm:px-6 lg:px-8 py-4 sm:py-6 lg:py-8 min-h-[calc(100vh-80px)] flex flex-col">
      {/* Content wrapper */}
      <div className="flex-1 flex flex-col">
        {/* Hero Section - Similar to Home Page alignment */}
        <div className="mb-6 sm:mb-8">
          {/* Title Section */}
          <div className="mt-2 sm:mt-4 lg:mt-6 mb-6">
            <h1 
              className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-normal leading-tight tracking-wide text-[#B8860B] mb-2 sm:mb-3" 
              style={{ fontFamily: 'Georgia, serif' }}
            >
              Book Gold
            </h1>
            <p className="text-gray-600 text-sm sm:text-base lg:text-lg max-w-xl">
              Invest in 22K purity gold easily and securely.
            </p>
          </div>
          
          {/* Stats Row - Live Rate + Total Bookings + 7-Day Trend */}
          <div className="flex flex-wrap gap-3 sm:gap-4">
            {/* Live Booking Rate Card - Same style as Total Bookings */}
            <div className="bg-white/50 backdrop-blur-sm rounded-xl p-4 border border-[#004D40]/20 shadow-sm min-w-[180px]">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[#004D40]/10 border border-[#004D40]/30 flex items-center justify-center flex-shrink-0">
                  <TrendingUp className="w-5 h-5 text-[#004D40]" />
                </div>
                <div>
                  <p className="text-[10px] font-semibold text-[#004D40] uppercase tracking-wide">Live Booking Rate</p>
                  <p className="text-xl font-bold text-gray-900">
                    ₹{buyRate.toFixed(2)} <span className="text-xs font-normal text-gray-500">/ Milli</span>
                  </p>
                  <div className="border-t border-gray-200 mt-1 pt-1">
                    <p className="text-[10px] text-gray-500">22KT: ₹{rate22kt.toLocaleString('en-IN')} / g</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Total Bookings Card */}
            <div className="bg-white/50 backdrop-blur-sm rounded-xl p-4 flex items-center gap-3 border border-[#004D40]/20 shadow-sm min-w-[160px]">
              <div className="w-10 h-10 rounded-full bg-[#004D40]/10 border border-[#004D40]/30 flex items-center justify-center flex-shrink-0">
                <Shield className="w-5 h-5 text-[#004D40]" />
              </div>
              <div>
                <p className="text-[10px] font-semibold text-[#004D40] uppercase tracking-wide">Total Bookings</p>
                {statsLoading ? (
                  <div className="animate-pulse h-5 bg-gray-200 rounded w-10"></div>
                ) : (
                  <p className="text-xl font-bold text-gray-900">{platformStats?.total_bookings_display || '0'}</p>
                )}
                <p className="text-[9px] text-gray-500">This Month</p>
              </div>
            </div>

            {/* 7-Day Trend Card */}
            <div className="bg-white/50 backdrop-blur-sm rounded-xl p-4 border border-[#004D40]/20 shadow-sm min-w-[200px]">
              <div className="flex items-center justify-between mb-1">
                <p className="text-[10px] font-semibold text-[#004D40] uppercase tracking-wide">7-Day Gold Trend</p>
                {!priceHistoryLoading && priceHistory.length > 1 && (
                  <div className={`flex items-center gap-1 text-[10px] font-medium ${isPositive ? 'text-green-600' : 'text-red-500'}`}>
                    {isPositive ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                    <span>{isPositive ? '+' : ''}{weekChange}%</span>
                  </div>
                )}
              </div>
              
              {/* Sparkline */}
              {priceHistoryLoading ? (
                <div className="h-8 flex items-center justify-center">
                  <Loader2 className="w-4 h-4 animate-spin text-[#004D40]" />
                </div>
              ) : priceHistory.length > 1 ? (
                <div className="relative h-8">
                  <svg className="w-full h-full" viewBox="0 0 160 30" preserveAspectRatio="none">
                    <path
                      d={`M 0 ${25 - ((priceHistory[0]?.price - minPrice) / priceRange) * 20} 
                          ${priceHistory.map((p, i) => `L ${i * (160 / (priceHistory.length - 1 || 1))} ${25 - ((p.price - minPrice) / priceRange) * 20}`).join(' ')}`}
                      fill="none"
                      stroke="#004D40"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <circle
                      cx={160}
                      cy={25 - ((priceHistory[priceHistory.length - 1]?.price - minPrice) / priceRange) * 20}
                      r="3"
                      fill="#004D40"
                    />
                  </svg>
                </div>
              ) : (
                <div className="h-8 flex items-center text-xs text-gray-500">No data</div>
              )}
            </div>
          </div>
        </div>

        {/* Main Grid - Input, Summary */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 lg:gap-6 max-w-4xl">
          {/* Left Column - Enter Amount + Milli Balance */}
          <div className="space-y-4">
            <PanThresholdBanner amount={amount} />
            {/* Enter Amount Card */}
            <div className="bg-white/90 rounded-2xl p-5 shadow-sm border border-amber-100">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Enter Amount</h2>
              
              {/* Amount Input */}
              <div className="relative mb-4">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-2xl text-gray-700 font-medium">₹</span>
                <input
                  type="text"
                  value={customAmount}
                  onChange={(e) => handleAmountChange(e.target.value)}
                  className="w-full pl-10 pr-4 py-4 text-2xl font-semibold text-gray-900 border border-amber-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#B8860B] focus:border-transparent bg-white"
                  placeholder="Enter amount"
                />
              </div>

              {/* Preset Amounts - Gold buttons - 8 options in 2 rows */}
              <div className="grid grid-cols-4 gap-2 mb-5">
                {presetAmounts.map((preset) => (
                  <button
                    key={preset}
                    onClick={() => handlePresetClick(preset)}
                    className={`py-2 sm:py-2.5 rounded-lg font-medium text-xs sm:text-sm transition-all ${
                      amount === preset
                        ? 'bg-gradient-to-r from-[#B8860B] to-[#DAA520] text-white shadow-md'
                        : 'bg-[#F5E6C8] text-[#8B6914] border border-[#B8860B]/30 hover:bg-[#EDD9B5]'
                    }`}
                  >
                    ₹{preset >= 1000 ? `${preset/1000}K` : preset}
                  </button>
                ))}
              </div>

              {/* Proceed to Pay Button - Gold with UPI */}
              <button
                onClick={handleProceedToPay}
                disabled={isLoading || rateLoading || amount < 1 || !buyRate}
                className="w-full py-4 bg-gradient-to-r from-[#B8860B] to-[#DAA520] text-white text-lg font-semibold rounded-xl shadow-lg hover:shadow-xl ring-2 ring-[#B8860B]/30 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Initiating Payment...
                  </>
                ) : rateLoading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Loading Rate...
                  </>
                ) : (
                  'Proceed to Pay via UPI'
                )}
              </button>

              {/* Security Note */}
              <div className="flex items-center justify-center gap-2 mt-3 text-gray-600">
                <Lock className="w-4 h-4" />
                <p className="text-sm font-medium">All transactions are secure and encrypted.</p>
              </div>
            </div>

            {/* Mini Milli Balance Card */}
            <div 
              className="rounded-xl p-4 shadow-[0_4px_20px_rgba(0,0,0,0.15),0_2px_8px_rgba(0,0,0,0.1)] relative overflow-hidden"
              style={{
                backgroundImage: 'url(/assets-new/balance-card-bg.webp)',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
              }}
            >
              <div className="flex items-center justify-between relative z-10">
                <div>
                  <p className="text-white/70 text-xs font-medium mb-1">Your Milli Balance</p>
                  <p className="text-2xl font-bold text-white">
                    {(user?.milli_balance || 0).toFixed(2)} <span className="text-sm font-normal text-white/80">Milli&apos;s</span>
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-white/60 text-xs">≈ {((user?.milli_balance || 0) / 1000).toFixed(4)} g</p>
                </div>
              </div>
            </div>
          </div>

          {/* Right Card - Booking Summary (Gold Theme) */}
          <div className="relative bg-[#F5E6C8] rounded-xl p-5 shadow-[inset_0_0_25px_rgba(139,90,43,0.12)] border-2 border-amber-400/60 flex flex-col">
            {/* Decorative corner accents */}
            <div className="absolute top-2 left-2 w-4 h-4 border-t-2 border-l-2 border-amber-600/70 rounded-tl-sm"></div>
            <div className="absolute top-2 right-2 w-4 h-4 border-t-2 border-r-2 border-amber-600/70 rounded-tr-sm"></div>
            <div className="absolute bottom-2 left-2 w-4 h-4 border-b-2 border-l-2 border-amber-600/70 rounded-bl-sm"></div>
            <div className="absolute bottom-2 right-2 w-4 h-4 border-b-2 border-r-2 border-amber-600/70 rounded-br-sm"></div>
            
            <h2 className="text-xl font-semibold text-amber-900 mb-5 text-center tracking-wide" style={{ fontFamily: 'Georgia, serif' }}>
              Booking Summary
            </h2>
            
            {/* Summary Items */}
            <div className="space-y-2.5 mb-4">
              <div className="flex justify-between items-center py-2 border-b border-dashed border-amber-400/40">
                <span className="text-amber-800">Amount</span>
                <span className="font-semibold text-amber-900">₹{amount.toFixed(2)}</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-dashed border-amber-400/40">
                <span className="text-amber-800">Live Booking Rate (per Milli)</span>
                {rateLoading ? (
                  <div className="animate-pulse h-5 bg-amber-300/40 rounded w-16"></div>
                ) : (
                  <span className="font-semibold text-amber-900">₹{buyRate.toFixed(2)}</span>
                )}
              </div>
              <div className="flex justify-between items-center py-2 border-b border-dashed border-amber-400/40">
                <span className="text-amber-800">Milli&apos;s You Will Get</span>
                {rateLoading ? (
                  <div className="animate-pulse h-5 bg-amber-300/40 rounded w-20"></div>
                ) : (
                  <span className="font-bold text-green-700">{millisYouGet.toFixed(2)} Milli&apos;s</span>
                )}
              </div>
              <div className="flex justify-between items-center py-2 border-b border-dashed border-amber-400/40">
                <span className="text-amber-800">Total Weight (approx.)</span>
                {rateLoading ? (
                  <div className="animate-pulse h-5 bg-amber-300/40 rounded w-24"></div>
                ) : (
                  <span className="font-bold text-green-700">{gramsYouGet.toFixed(4)} grams</span>
                )}
              </div>
              <div className="flex justify-between items-center py-2 border-b border-dashed border-amber-400/40">
                <span className="text-amber-800">Total Balance After Booking</span>
                {rateLoading ? (
                  <div className="animate-pulse h-5 bg-amber-300/40 rounded w-24"></div>
                ) : (
                  <span className="font-bold text-[#004D40]">{((user?.milli_balance || 0) + millisYouGet).toFixed(2)} Milli&apos;s</span>
                )}
              </div>
            </div>

            {/* Disclaimer */}
            <p className="text-sm text-amber-700/70 text-center italic mb-3" style={{ fontFamily: 'Georgia, serif' }}>
              Price may vary with live gold rate.
            </p>

            {/* Rotating Tip */}
            <div className="flex-1 flex items-end justify-center">
              <p className="text-xs text-amber-700/80 text-center italic px-2" style={{ fontFamily: 'Georgia, serif' }}>
                {currentTip}
              </p>
            </div>
          </div>
        </div>

        {/* Spacer to push banner to bottom */}
        <div className="flex-1"></div>

        {/* Trust Badges Row - Same background as Booking Summary card */}
        <div className="flex flex-col xl:flex-row gap-4 mt-6 mx-0">
          <div className="flex-1 bg-[#F5E6C8] rounded-xl p-4 border-2 border-amber-400/60 shadow-[inset_0_0_25px_rgba(139,90,43,0.12)] flex items-center justify-center">
            <div className="flex justify-center items-center gap-6 lg:gap-10 flex-wrap">
              {/* Trust Badge 1 - 100% Secure */}
              <div className="flex items-center gap-2">
                <div className="w-9 h-9 rounded-full bg-amber-600/10 border border-amber-600/40 flex items-center justify-center flex-shrink-0">
                  <Lock className="w-4 h-4 text-amber-800" />
                </div>
                <div>
                  <p className="text-xs font-semibold text-amber-900 uppercase tracking-wide">100% Secure</p>
                  <p className="text-[10px] text-amber-700/70">Bank-grade encryption</p>
                </div>
              </div>

              {/* Trust Badge 2 - Auto Rate Update */}
              <div className="flex items-center gap-2">
                <div className="w-9 h-9 rounded-full bg-amber-600/10 border border-amber-600/40 flex items-center justify-center flex-shrink-0">
                  <RefreshCw className="w-4 h-4 text-amber-800" />
                </div>
                <div>
                  <p className="text-xs font-semibold text-amber-900 uppercase tracking-wide">Auto Rate Update</p>
                  <p className="text-[10px] text-amber-700/70">Live market rates</p>
                </div>
              </div>

              {/* Trust Badge 3 - 24/7 Booking */}
              <div className="flex items-center gap-2">
                <div className="w-9 h-9 rounded-full bg-amber-600/10 border border-amber-600/40 flex items-center justify-center flex-shrink-0">
                  <Shield className="w-4 h-4 text-amber-800" />
                </div>
                <div>
                  <p className="text-xs font-semibold text-amber-900 uppercase tracking-wide">24/7 Booking</p>
                  <p className="text-[10px] text-amber-700/70">Anytime, anywhere</p>
                </div>
              </div>

              {/* Trust Badge 4 - Digital Receipt */}
              <div className="flex items-center gap-2">
                <div className="w-9 h-9 rounded-full bg-amber-600/10 border border-amber-600/40 flex items-center justify-center flex-shrink-0">
                  <ShieldCheck className="w-4 h-4 text-amber-800" />
                </div>
                <div>
                  <p className="text-xs font-semibold text-amber-900 uppercase tracking-wide">Digital Receipt</p>
                  <p className="text-[10px] text-amber-700/70">Instant & trackable</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* UPI Payment Modal */}
      <UPIPaymentModal
        isOpen={showPaymentModal}
        onClose={handlePaymentModalClose}
        paymentData={paymentData}
        onSuccess={handlePaymentSuccess}
      />
    </div>
  );
};

export default BookGoldPage;
